import { formatDistanceToNow } from 'date-fns';
import { ko } from 'date-fns/locale';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, CountUp } from '../../components/ui';
import { useDashboardStats } from '../../hooks/useDashboardStats';

const Dashboard: React.FC = () => {
  const { data: stats, isLoading, error } = useDashboardStats();
  const navigate = useNavigate();

  const handleActivityClick = (activity: any) => {
    switch (activity.type) {
      case 'SIGNUP':
        if (activity.targetId) navigate(`/dashboard/users/${activity.targetId}`);
        break;
      case 'ORDER':
        if (activity.targetId) navigate(`/dashboard/payments/${activity.targetId}`);
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 w-1/4 rounded bg-slate-200"></div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 rounded-xl bg-slate-200"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="h-96 rounded-xl bg-slate-200"></div>
            <div className="h-96 rounded-xl bg-slate-200"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-center text-red-500">데이터를 불러오는 중 오류가 발생했습니다.</div>;
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-8 p-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">대시보드</h1>
        <p className="mt-1 text-slate-500">Lumos 관리자 시스템 현황입니다.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">총 사용자</p>
              <h3 className="mt-2 text-2xl font-bold whitespace-nowrap text-slate-900 tabular-nums">
                <CountUp end={stats?.totalUsers || 0} suffix="명" duration={500} />
              </h3>
            </div>
            <div
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                (stats?.totalUsersChange || 0) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
              {(stats?.totalUsersChange || 0) >= 0 ? '+' : ''}
              {stats?.totalUsersChange}%
            </div>
          </div>
          <div className="mt-4 h-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-blue-500" style={{ width: '70%' }}></div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">오늘 신규 가입</p>
              <h3 className="mt-2 text-2xl font-bold whitespace-nowrap text-slate-900 tabular-nums">
                <CountUp end={stats?.todayNewUsers || 0} suffix="명" duration={500} />
              </h3>
            </div>
            <div
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                (stats?.todayNewUsersChange || 0) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
              {(stats?.todayNewUsersChange || 0) >= 0 ? '+' : ''}
              {stats?.todayNewUsersChange}%
            </div>
          </div>
          <div className="mt-4 h-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-500" style={{ width: '45%' }}></div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">이번달 매출</p>
              <h3 className="mt-2 text-2xl font-bold whitespace-nowrap text-slate-900 tabular-nums">
                <CountUp end={stats?.revenueThisMonth || 0} suffix="원" duration={500} />
              </h3>
            </div>
            <div
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                (stats?.revenueChange || 0) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
              {(stats?.revenueChange || 0) >= 0 ? '+' : ''}
              {stats?.revenueChange}%
            </div>
          </div>
          <div className="mt-4 h-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-indigo-500" style={{ width: '60%' }}></div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">총 주문</p>
              <h3 className="mt-2 text-2xl font-bold whitespace-nowrap text-slate-900 tabular-nums">
                <CountUp end={stats?.orders || 0} suffix="건" duration={500} />
              </h3>
            </div>
            <div
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                (stats?.ordersChange || 0) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
              {(stats?.ordersChange || 0) >= 0 ? '+' : ''}
              {stats?.ordersChange}%
            </div>
          </div>
          <div className="mt-4 h-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-orange-500" style={{ width: '85%' }}></div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">최근 활동</h3>
          </div>
          <div className="space-y-4">
            {stats?.recentActivities?.map((activity) => (
              <div
                key={`${activity.type}-${activity.id}`}
                className="flex cursor-pointer items-start gap-4 rounded-lg p-3 transition-colors hover:bg-slate-50"
                onClick={() => handleActivityClick(activity)}>
                <div
                  className={`rounded-lg p-2 ${
                    activity.type === 'SIGNUP'
                      ? 'bg-blue-50 text-blue-600'
                      : activity.type === 'ORDER'
                        ? 'bg-indigo-50 text-indigo-600'
                        : 'bg-orange-50 text-orange-600'
                  }`}>
                  {activity.type === 'SIGNUP' && (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    </svg>
                  )}
                  {activity.type === 'ORDER' && (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{activity.title}</p>
                  <p className="truncate text-sm text-slate-500">{activity.description}</p>
                </div>
                <div className="text-xs whitespace-nowrap text-slate-400">
                  {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true, locale: ko })}
                </div>
              </div>
            ))}
            {(!stats?.recentActivities || stats.recentActivities.length === 0) && (
              <div className="py-8 text-center text-slate-500">최근 활동이 없습니다.</div>
            )}
          </div>
        </div>

        <Card>
          <h3 className="mb-6 text-lg font-bold text-slate-900">빠른 작업</h3>
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="flex h-auto flex-col gap-2 py-4 hover:bg-slate-50"
              onClick={() => navigate('/dashboard/courses')}>
              <span className="text-2xl">📚</span>
              <span className="text-sm font-medium">강의 관리</span>
            </Button>
            <Button
              variant="outline"
              className="flex h-auto flex-col gap-2 py-4 hover:bg-slate-50"
              onClick={() => navigate('/dashboard/users')}>
              <span className="text-2xl">👥</span>
              <span className="text-sm font-medium">사용자 관리</span>
            </Button>
            <Button
              variant="outline"
              className="flex h-auto flex-col gap-2 py-4 hover:bg-slate-50"
              onClick={() => navigate('/dashboard/payments')}>
              <span className="text-2xl">💰</span>
              <span className="text-sm font-medium">매출 조회</span>
            </Button>
            <Button
              variant="outline"
              className="flex h-auto flex-col gap-2 py-4 hover:bg-slate-50"
              onClick={() => navigate('/dashboard/settings')}>
              <span className="text-2xl">⚙️</span>
              <span className="text-sm font-medium">설정</span>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
